// El catálogo de SERVICIOS del venue (FRA-347): todo lo que puede entrar a
// una cotización, sabiendo lo que cuesta.
//
// UNA PREGUNTA: ¿quién lo pone? Un servicio solo necesita nombre y precio.
//
//   El venue      → `proveedorId: null`, y opcionalmente `itemizado` +
//                   `escalaUnidades`: piezas tuyas con estado y reserva.
//   Un proveedor  → `proveedorId` + `costoProveedor`. Sin piezas.
//
// SON EXCLUYENTES. Administrar piezas significa que son tuyas: les sigues el
// estado y bloqueas que se usen en dos eventos a la vez. Si el proveedor
// trae el equipo, su disponibilidad es problema suyo y lo que el evento
// genera es su costo, no una reserva. Con las dos prendidas el mismo
// servicio salía en las dos secciones del evento —contratar al proveedor Y
// apartar tus piezas para lo mismo—, que son mecánicas que se estorban.
// `docDesdeForm` lo normaliza: con proveedor, `itemizado` es false.
//
// HUBO UNA TERCERA —"¿consume algo que se repone?"— con existencias, consumo
// imputado por evento y costo unitario con historial. Se retiró: exigía
// registrar cada consumo para no mentir, y un inventario que nadie alimenta
// da números peores que no tenerlo. Lo que se compra y lo que se recarga se
// captura como gasto (general en Cuentas o del evento), que es lo que los
// venues hacían de todos modos.
//
// `adquisicion` (cuánto costó comprarlo) es historia, no clasificación: se
// captura si se conoce y alimenta la recuperación de la inversión.
//
// PRECIOS (FRA-349): un número por lado y ya. `precioUnitario` es lo que le
// cobras al cliente por unidad; `costoProveedor`, lo que te cobran a ti.
// Hubo un motor de reglas apilables y se retiró: era demasiado aparato para
// capturar un precio. Lo que no cabe en un número se ajusta a mano en la
// línea de la cotización. La tarifa YA NO vive en el proveedor: un proveedor
// da varios servicios con precios distintos, y el servicio es lo que se
// contrata.
//
// VARIANTES (FRA-350): "Renta de salón" con martes / sábado / con luces es un
// solo renglón con tres variantes, cada una con su precio (y su tarifa de
// costo si difiere). Todo lo demás lo heredan del padre.
//
// ESCALAS (FRA-351): cómo se mide el estado de las piezas físicas lo define
// el servicio — palabras propias, un porcentaje, o nada — no un enum fijo.
// El default describe condición (bueno / mantenimiento / fuera de servicio);
// un venue que mida llenado puede poner sus propias palabras.
//
// SE REUSA LA COLECCIÓN `conceptos` a propósito. Los venues ya tienen su
// catálogo cargado; un doc viejo es solo {nombre, precioUnitario} — que es
// exactamente la forma a la que volvió el precio. Cero migración.
//
// Las piezas del servicio viven en conceptos/{id}/unidades. Las escrituras
// están en utils/inventario.js.

export const UNIDADES = [
  { value: "evento", label: "Por evento" },
  { value: "pieza", label: "Por pieza" },
  { value: "hora", label: "Por hora" },
  { value: "dia", label: "Por día" },
  { value: "persona", label: "Por persona" },
  { value: "otra", label: "Otra…" },
];

const UNIDAD_SINGULAR = {
  evento: "evento",
  pieza: "pieza",
  hora: "hora",
  dia: "día",
  persona: "persona",
};

// ── Las dos dimensiones ──────────────────────────────────────────────
// Lectura tolerante: todo asume que el doc puede ser viejo (solo nombre y
// precio) y responde algo sensato igual.

export const proveedorDe = (item) => item?.proveedorId || null;
export const esDelVenue = (item) => !proveedorDe(item);
// Excluyente con el proveedor: lo que trae un tercero no lleva piezas tuyas.
// Se resuelve también en la lectura para que un doc que quedara con las dos
// se comporte igual en todas las pantallas. Una renta tampoco lleva piezas:
// lo que se renta es el espacio, y lo que va dentro son sus incluidos.
export const esItemizado = (item) =>
  !!item?.itemizado && !proveedorDe(item) && !esRenta(item);
export const adquisicionDe = (item) => item?.adquisicion || null;

export const llevaInventario = (item) => esItemizado(item);

export const esCobrable = (item) => item?.cobrable !== false;

export const esDescuento = (item) => (item?.tipo || "normal") === "descuento";

// ── Rentas (tipo "renta") ────────────────────────────────────────────
// La renta de un espacio es un servicio como cualquier otro —se cobra, tiene
// unidad y variantes— con una sola cosa propia: la lista de servicios que
// trae INCLUIDOS. Un sábado grande son varias rentas (viernes de montaje,
// sábado del evento, domingo de desmontaje), cada una con su precio y su
// propia lista: no hay "la renta del venue" en singular ni límite de cuántas
// caben en una cotización.
//
// "Incluido" es del par renta-servicio, NO del servicio. Las mismas sillas
// van incluidas en la renta del sábado y se cobran aparte en otro evento, así
// que la lista vive en la renta y los hijos son servicios normales, con su
// proveedor, su tarifa y sus piezas intactas.
//
// `cobrable: false` sigue significando lo que significaba —"no se cobra por
// separado"— y es lo que distingue la limpieza de baños (existe solo para ir
// dentro de rentas, fuera del picker) del valet parking (se cotiza solo).
export const esRenta = (item) => (item?.tipo || "normal") === "renta";

export const TIPOS_SERVICIO = ["normal", "renta", "descuento"];

// Los servicios que una renta trae: [{conceptoId, cantidad}]. Lectura
// tolerante de ids sueltos por si alguna pantalla los captura así.
export function incluyeDe(item) {
  const raw = Array.isArray(item?.incluye) ? item.incluye : [];
  const vistos = new Set();
  const out = [];
  raw.forEach((x) => {
    const entrada = typeof x === "string" ? { conceptoId: x } : x;
    const id = entrada?.conceptoId;
    if (!id || vistos.has(id)) return;
    vistos.add(id);
    const cantidad = Number(entrada.cantidad);
    out.push({ conceptoId: id, cantidad: cantidad > 0 ? cantidad : 1 });
  });
  return out;
}

// Las rentas en las que un servicio va incluido. Es un scan sobre el catálogo
// —que todas las pantallas ya tienen suscrito— y no un campo en el hijo: un
// puntero de vuelta sería un segundo lugar donde la verdad puede quedar a
// medias, y el mismo servicio va en varias rentas.
export const rentasQueIncluyen = (items, conceptoId) =>
  !conceptoId
    ? []
    : (Array.isArray(items) ? items : []).filter(
        (r) =>
          esRenta(r) &&
          !r?.archivedAt &&
          incluyeDe(r).some((i) => i.conceptoId === conceptoId)
      );

// Qué puede ir dentro de una renta: SOLO lo que está marcado "incluido en la
// renta" (`cobrable: false`). Ni otra renta (un nivel, y punto), ni un
// descuento, ni ella misma.
//
// La marca es la línea divisoria, y es la que el venue ya conoce: una
// ambulancia o un valet son servicios del venue, pero se COBRAN aparte — la
// renta no los trae, y poder meterlos dentro sería prometer gratis lo que se
// vende. Lo que sí trae —limpieza de baños, personal de montaje, las sillas—
// existe precisamente para ir dentro, y por eso está marcado.
//
// Consecuencia a propósito: un servicio es lo uno o lo otro, no los dos. Para
// que algo pase a ir incluido, se marca en su ficha y deja de cotizarse solo.
export const puedeIrIncluido = (item, rentaId = null) =>
  !!item?.id &&
  !item.archivedAt &&
  item.id !== rentaId &&
  !esRenta(item) &&
  !esDescuento(item) &&
  !esCobrable(item);

// Las rentas del catálogo, para pintarlas juntas y para saber si el venue ya
// tiene alguna.
export const rentasDe = (items) =>
  (Array.isArray(items) ? items : []).filter((c) => esRenta(c) && !c?.archivedAt);

// Servicios marcados "incluido en la renta" (`cobrable: false`) que no viven
// dentro de ninguna renta. Antes de que la renta existiera como tipo, la
// casilla era el único lugar donde decir "esto no se cobra solo", así que
// quedaron sueltos: la limpieza de baños existía pero nada la ligaba al salón,
// y por eso no aparecía en ninguna cotización. Son lo que el asistente del
// catálogo acomoda en su segunda pasada.
export function incluidosSinRenta(items) {
  const lista = Array.isArray(items) ? items : [];
  const dentro = new Set();
  lista.forEach((c) => {
    if (esRenta(c) && !c?.archivedAt) {
      incluyeDe(c).forEach((i) => dentro.add(i.conceptoId));
    }
  });
  return lista.filter(
    (c) =>
      c?.id &&
      !c.archivedAt &&
      !esRenta(c) &&
      !esDescuento(c) &&
      c.cobrable === false &&
      !dentro.has(c.id)
  );
}

// Un servicio está clasificado cuando alguien ya contestó las tres preguntas
// (aunque las tres sean "no"). Un doc viejo sigue siendo un servicio válido
// —del venue, sin piezas, sin consumo— pero el asistente ofrece revisarlo.
export const estaClasificado = (item) =>
  !!item?.clasificadoEn || !!proveedorDe(item) || esItemizado(item) || esRenta(item);

export const pendientesDeClasificar = (items) =>
  (Array.isArray(items) ? items : [])
    .filter((i) => !i?.archivedAt)
    // Un descuento no es un servicio: nunca hay nada que clasificarle.
    .filter((i) => !esDescuento(i))
    .filter((i) => !estaClasificado(i));

export const unidadDe = (item) => {
  const u = item?.unidad;
  if (!u || u === "otra") return item?.unidadNombre || "unidad";
  return UNIDAD_SINGULAR[u] || item?.unidadNombre || "unidad";
};

// Chips que resumen las dimensiones: "Del venue" / "Lo da Ambulancias Vital"
// / "Piezas identificadas" / "Consume propano".
export function describeDimensiones(item, { proveedoresPorId = {} } = {}) {
  const chips = [];
  if (esRenta(item)) {
    const n = incluyeDe(item).length;
    chips.push({ tipo: "renta", label: "Renta" });
    if (n > 0) {
      chips.push({ tipo: "incluye", label: `Incluye ${n} ${n === 1 ? "servicio" : "servicios"}` });
    }
    return chips;
  }
  const pid = proveedorDe(item);
  if (pid) {
    const nombre = proveedoresPorId[pid]?.nombre || item?.proveedorNombre || "proveedor";
    chips.push({ tipo: "proveedor", label: `Lo da ${nombre}` });
  } else {
    chips.push({ tipo: "venue", label: "Del venue" });
  }
  if (esItemizado(item)) chips.push({ tipo: "piezas", label: "Piezas identificadas" });
  return chips;
}

// ── Precios: cobro y costo (FRA-349) ─────────────────────────────────

const redondea2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Los dos lados son un número por unidad, nada más. Hubo un motor de reglas
// (base + escalones + porcentajes, apilables) y no valió lo que costaba:
// capturar un precio se volvió un formulario de reglas para un negocio que
// cobra por hora, por pieza o por evento. Lo que no cabe en un número se
// ajusta a mano en la línea de la cotización (`lineaAjustada`) o se captura
// como costo del evento.
//
// Lectura tolerante de los servicios que alcanzaron a guardarse con el motor:
// el cobro siempre tuvo `precioUnitario` sincronizado, así que se lee solo;
// del costo se rescata el primer monto de la estructura vieja.
const montoDeReglasViejas = (reglas) => {
  if (!Array.isArray(reglas)) return 0;
  for (const r of reglas) {
    const v = Number(r?.precio ?? r?.monto);
    if (v > 0) return v;
  }
  return 0;
};

// Lo que le cobras al cliente por UNA unidad.
export const precioDe = (item) => Number(item?.precioUnitario) || 0;

// Lo que el proveedor te cobra por UNA unidad.
export const costoProveedorDe = (item) => {
  if (item?.costoProveedor != null) return Number(item.costoProveedor) || 0;
  return montoDeReglasViejas(item?.estructuraCosto);
};

export const tieneCosto = (item) => costoProveedorDe(item) > 0;

// Lo que se le cobra al cliente por `cantidad` unidades: precio × cantidad.
export const cobroDe = (item, cantidad = 1) =>
  redondea2(precioDe(item) * (Number(cantidad) || 0));

// Lo que cuesta contratar `cantidad` unidades: tarifa × cantidad.
export const costoDe = (item, cantidad = 1) =>
  redondea2(costoProveedorDe(item) * (Number(cantidad) || 0));

// ── Variantes (FRA-350) ──────────────────────────────────────────────
// Mismo concepto, precio distinto según la condición. Heredan del padre
// proveedor y piezas; solo tienen precio y tarifa propios.

export const variantesDe = (item) =>
  Array.isArray(item?.variantes) ? item.variantes : [];

export const varianteDe = (item, varianteId) =>
  varianteId ? variantesDe(item).find((v) => v?.id === varianteId) || null : null;

// El precio/tarifa propios de la variante, o los del servicio si no los
// tiene. `null` o 0 = hereda (no existe "cuesta cero" capturado a propósito).
export const precioDeVariante = (item, variante) => {
  const propio = Number(variante?.precioUnitario) || 0;
  return propio > 0 ? propio : precioDe(item);
};

export const costoProveedorDeVariante = (item, variante) => {
  const propio = Number(variante?.costoProveedor) || 0;
  return propio > 0 ? propio : costoProveedorDe(item);
};

export const cobroDeVariante = (item, variante, cantidad = 1) =>
  redondea2(precioDeVariante(item, variante) * (Number(cantidad) || 0));

export const costoDeVariante = (item, variante, cantidad = 1) =>
  redondea2(costoProveedorDeVariante(item, variante) * (Number(cantidad) || 0));

export const nombreConVariante = (item, variante) =>
  variante?.nombre ? `${item?.nombre || ""} · ${variante.nombre}` : item?.nombre || "";

// Id corto y estable para una variante nueva. Se genera en el cliente
// porque las variantes viven como array dentro del doc del servicio.
export const nuevoIdVariante = () =>
  `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function nuevaVariante({ nombre, precioUnitario = null, costoProveedor = null, id = null }) {
  const precio = Number(precioUnitario) || 0;
  const costo = Number(costoProveedor) || 0;
  return {
    id: id || nuevoIdVariante(),
    nombre: String(nombre || "").trim(),
    // null = hereda el del servicio.
    precioUnitario: precio > 0 ? precio : null,
    costoProveedor: costo > 0 ? costo : null,
  };
}

// ── Cómo se administran las piezas: identificadas o por cantidad ─────
// Hay dos realidades y solo una estaba cubierta.
//
//   IDENTIFICADAS ("Calentador 03"): cada pieza es un doc en `unidades`, con
//   su estado y su nivel, y se reserva por pieza —el bloqueo es duro, en
//   transacción, porque importa CUÁL sale a cada evento.
//
//   POR CANTIDAD (240 sillas): nadie va a dar de alta 240 docs, y da igual
//   cuál silla sale. Lo que importa es cuántas hay y cuántas quedan libres ese
//   día. Se guarda un total y los renglones de lo que está fuera de
//   circulación, con el nombre que el venue quiera —"Mantenimiento", "Bodega
//   Cuernavaca", "Prestadas"—, y todo eso resta de lo reservable.
//
// El estado por pieza (`ESTADOS_UNIDAD`) NO se vuelve texto libre: de ese enum
// depende el bloqueo de doble uso de las piezas identificadas. Lo que es libre
// es el nombre de los renglones del modo por cantidad, que no bloquean nada:
// solo bajan el total.
//
// OJO: esto es un CONTEO, y un conteo miente si nadie lo actualiza —que es por
// lo que se retiró el inventario de consumibles—. La diferencia es que aquí
// son cuatro números que el owner edita cuando la realidad cambia (se fueron
// 15 sillas a Cuernavaca), no un registro que haya que alimentar evento por
// evento: LO OCUPADO NUNCA SE CAPTURA, sale de los eventos. Si se abandona, el
// peor caso es que diga que tienes más sillas libres de las que tienes; nunca
// que los números del negocio salgan mal.

export const MODOS_PIEZAS = [
  {
    value: "identificadas",
    label: "Identificadas una por una",
    hint: "Cada pieza tiene nombre y estado: calentadores, luces, mobiliario caro.",
  },
  {
    value: "cantidad",
    label: "Por cantidad",
    hint: "Da igual cuál sale: 240 sillas, 40 mesas. Solo importa cuántas quedan libres.",
  },
];

// Lectura tolerante: un servicio itemizado de antes de esto administra piezas
// identificadas, que es lo único que existía.
export const modoPiezasDe = (item) =>
  item?.modoPiezas === "cantidad" ? "cantidad" : "identificadas";

export const esPool = (item) => esItemizado(item) && modoPiezasDe(item) === "cantidad";
export const esIdentificado = (item) => esItemizado(item) && !esPool(item);

export const piezasTotalesDe = (item) =>
  esPool(item) ? Math.max(0, Math.floor(Number(item?.piezasTotales) || 0)) : 0;

// Renglones de lo que existe pero no se puede reservar. `nombre` es del venue.
export function noDisponiblesDe(item) {
  if (!esPool(item)) return [];
  return (Array.isArray(item?.noDisponibles) ? item.noDisponibles : [])
    .map((x) => ({
      id: x?.id || null,
      nombre: String(x?.nombre || "").trim(),
      cantidad: Math.max(0, Math.floor(Number(x?.cantidad) || 0)),
    }))
    .filter((x) => x.nombre && x.cantidad > 0);
}

export const totalNoDisponible = (item) =>
  noDisponiblesDe(item).reduce((s, x) => s + x.cantidad, 0);

// Cuántas se pueden comprometer: el total menos lo que está fuera.
export const piezasReservablesDe = (item) =>
  Math.max(0, piezasTotalesDe(item) - totalNoDisponible(item));

export const nuevoIdNoDisponible = () =>
  `nd${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// ── Escalas de estado de las piezas (FRA-351) ────────────────────────
// `escalaUnidades`: { tipo: "ninguna" | "etiquetas" | "porcentaje", etiquetas? }.
// El trío de siempre es solo la propuesta por default al activar la opción.

export const ESCALA_DEFAULT = {
  tipo: "etiquetas",
  etiquetas: ["Bueno", "Requiere mantenimiento", "Fuera de servicio"],
};

export const TIPOS_ESCALA = [
  { value: "ninguna", label: "Sin nivel", hint: "Solo importa si la pieza está activa o en mantenimiento." },
  { value: "etiquetas", label: "Por palabra", hint: "Lleno / medio / vacío, o las que tú definas." },
  { value: "porcentaje", label: "Por porcentaje", hint: "0 a 100 % de llenado." },
];

export function escalaDe(item) {
  // El nivel se mide POR PIEZA; en el modo por cantidad no hay pieza que medir.
  if (esPool(item)) return null;
  const e = item?.escalaUnidades;
  if (!e || !e.tipo || e.tipo === "ninguna") return null;
  if (e.tipo === "porcentaje") return { tipo: "porcentaje" };
  if (e.tipo === "etiquetas") {
    const etiquetas = (Array.isArray(e.etiquetas) ? e.etiquetas : [])
      .map((x) => String(x || "").trim())
      .filter(Boolean);
    return { tipo: "etiquetas", etiquetas: etiquetas.length ? etiquetas : ESCALA_DEFAULT.etiquetas };
  }
  return null;
}

export const tieneEscala = (item) => !!escalaDe(item);

// El valor "lleno" de la escala: es lo que queda una pieza al recargarla.
export function nivelLlenoDe(item) {
  const e = escalaDe(item);
  if (!e) return null;
  return e.tipo === "porcentaje" ? 100 : e.etiquetas[0];
}

// El valor "vacío": el último escalón. Sirve para señalar piezas por recargar.
export function nivelVacioDe(item) {
  const e = escalaDe(item);
  if (!e) return null;
  return e.tipo === "porcentaje" ? 0 : e.etiquetas[e.etiquetas.length - 1];
}

// Opciones para un selector de nivel, en el orden de la escala.
export function nivelesDe(item) {
  const e = escalaDe(item);
  if (!e) return [];
  if (e.tipo === "porcentaje") return [100, 75, 50, 25, 0];
  return e.etiquetas;
}

export function nivelLabel(item, nivel) {
  const e = escalaDe(item);
  if (!e || nivel == null || nivel === "") return null;
  if (e.tipo === "porcentaje") return `${Math.max(0, Math.min(100, Number(nivel) || 0))}%`;
  return String(nivel);
}

export function esNivelValido(item, nivel) {
  const e = escalaDe(item);
  if (!e) return nivel == null || nivel === "";
  if (e.tipo === "porcentaje") {
    const n = Number(nivel);
    return Number.isFinite(n) && n >= 0 && n <= 100;
  }
  return e.etiquetas.includes(String(nivel));
}

// Una pieza está "por recargar" si su nivel es el último de la escala (o 0%).
export function estaVacia(item, nivel) {
  const e = escalaDe(item);
  if (!e || nivel == null) return false;
  if (e.tipo === "porcentaje") return (Number(nivel) || 0) <= 0;
  return String(nivel) === e.etiquetas[e.etiquetas.length - 1];
}

// ── Paquetes ─────────────────────────────────────────────────────────
// "6 calentadores por $8,000" contra $1,500 la pieza. El paquete guarda su
// precio total; el descuento es la diferencia contra la lista, y se calcula
// —no se captura— para que no pueda quedar inconsistente.

export const paquetesDe = (item) =>
  Array.isArray(item?.paquetes) ? item.paquetes : [];

export function descuentoDePaquete(item, paquete) {
  const cantidad = Number(paquete?.cantidad) || 0;
  const precioPaquete = Number(paquete?.precio) || 0;
  const precioLista = cobroDe(item, cantidad);
  const descuento = precioLista - precioPaquete;
  return {
    cantidad,
    precioLista,
    precioPaquete,
    descuento,
    pct: precioLista > 0 ? descuento / precioLista : 0,
    // Un "paquete" más caro que la lista no es un error que haya que
    // bloquear (puede incluir instalación), pero sí algo que la UI señala.
    esRecargo: descuento < 0,
  };
}

// ── De catálogo a línea de cotización ────────────────────────────────
// Un solo lugar arma la línea del desglose, para que el precio y el enlace
// al catálogo no se puedan capturar por separado y separarse después.
//
// La línea es siempre units × unitPrice. `cantidadReal` existe por los
// paquetes: "6 calentadores" entra como 1 línea pero consumió 6 piezas.
export function lineaDesdeItem(item, { unidades = 1, paqueteId = null, varianteId = null } = {}) {
  if (!item) return null;
  const paquete = paqueteId
    ? paquetesDe(item).find((p) => p.id === paqueteId)
    : null;

  if (paquete) {
    return {
      concept: `${item.nombre} · ${paquete.nombre || `${paquete.cantidad} ${unidadDe(item)}`}`,
      units: 1,
      unitPrice: Number(paquete.precio) || 0,
      conceptoId: item.id,
      paqueteId: paquete.id,
      // Se guarda para poder imputar consumo/uso a la cantidad real, no a
      // "1 paquete".
      cantidadReal: Number(paquete.cantidad) || 0,
    };
  }

  const n = Number(unidades) || 1;
  const variante = varianteDe(item, varianteId);
  const precio = precioDeVariante(item, variante);
  return {
    concept: nombreConVariante(item, variante),
    conceptoId: item.id,
    ...(variante ? { varianteId: variante.id } : {}),
    cantidadReal: n,
    units: n,
    // Un descuento siempre entra negativo, venga como venga capturado.
    unitPrice: esDescuento(item) ? -Math.abs(precio) : precio,
  };
}

// Cuántas unidades del servicio consumió realmente una línea (un paquete de 6
// son 6, aunque la línea diga "1").
export const cantidadRealDeLinea = (linea) =>
  Number(linea?.cantidadReal) || Number(linea?.units) || 0;



// Línea ligada a un servicio pero con precio negociado a mano (FRA-352): la
// cantidad real y el enlace al catálogo se conservan —para el margen y el
// consumo— pero el precio ya no sale de la estructura. `precioAjustado`
// deja constancia de que alguien lo cambió a propósito.
export function lineaAjustada(item, { unidades = 1, varianteId = null, unitPrice = 0 } = {}) {
  if (!item) return null;
  const n = Number(unidades) || 1;
  const variante = varianteDe(item, varianteId);
  const precio = Number(unitPrice) || 0;
  return {
    concept: nombreConVariante(item, variante),
    conceptoId: item.id,
    ...(variante ? { varianteId: variante.id } : {}),
    cantidadReal: n,
    units: n,
    unitPrice: esDescuento(item) ? -Math.abs(precio) : precio,
    precioAjustado: true,
  };
}

// ── La renta y sus incluidos en el desglose ──────────────────────────
// Lo incluido NO se guarda dentro de la línea de la renta: cada uno es una
// LÍNEA NORMAL del desglose —con su `conceptoId` y su cantidad real— en $0 y
// marcada con `incluidoEn`. Dos razones:
//
//   - el cliente lo lee como renglón propio, con "Incluido" en el precio, que
//     es cómo se entrega una cotización;
//   - el costo del proveedor de un incluido (la limpieza que subcontratas)
//     viaja por el camino que YA existe —`respaldosDeCotizacion` lee
//     `linea.conceptoId` y nunca mira el precio—, en vez de necesitar una
//     segunda pasada que, si se olvida, deja el evento con margen inventado.
//
// La línea de la renta lleva `grupoId` y sus incluidos `incluidoEn: grupoId`,
// para poder editarlos, moverlos y borrarlos juntos. Una línea sin ninguno de
// los dos campos —todas las que ya existen— se comporta exactamente como antes.

export const nuevoIdGrupo = () =>
  `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const esLineaIncluida = (linea) => !!linea?.incluidoEn;

export const lineasIncluidasDe = (desglose, grupoId) =>
  !grupoId
    ? []
    : (Array.isArray(desglose) ? desglose : []).filter((l) => l?.incluidoEn === grupoId);

// Una línea de servicio incluido: cantidad real, sin precio.
export function lineaIncluida(item, { unidades = 1, grupoId = null } = {}) {
  if (!item?.id || !grupoId) return null;
  const n = Number(unidades) > 0 ? Number(unidades) : 1;
  return {
    concept: item.nombre || "",
    conceptoId: item.id,
    cantidadReal: n,
    units: n,
    unitPrice: 0,
    incluidoEn: grupoId,
  };
}

// El grupo listo para insertarse: la renta y sus incluidos. `lineaRenta` es lo
// que ya devuelven `lineaDesdeItem` / `lineaAjustada`; `incluidos` es
// [{item, cantidad}] con lo que quedó seleccionado.
export function grupoDeRenta(lineaRenta, incluidos = [], { grupoId = null } = {}) {
  if (!lineaRenta) return [];
  const id = grupoId || nuevoIdGrupo();
  const hijos = (Array.isArray(incluidos) ? incluidos : [])
    .map(({ item, cantidad }) => lineaIncluida(item, { unidades: cantidad, grupoId: id }))
    .filter(Boolean);
  // Una renta que no trae nada es una línea normal: no carga un id que nada
  // usa, y así deja de ser un grupo en cuanto se desmarca el último incluido.
  if (hijos.length === 0) return [lineaRenta];
  return [{ ...lineaRenta, grupoId: id }, ...hijos];
}

// ── Validación del alta / edición / clasificación ─────────────────────
// La lista vive aquí y no en el formulario para que las tres pantallas
// exijan exactamente lo mismo (FRA-348).
//
// Nada es obligatorio más que el nombre. El PRECIO lo es también salvo que
// se diga que está incluido en la renta del venue: un renglón sin precio no
// puede entrar a una cotización. Quién lo pone —el venue o un proveedor— no
// se exige: sin proveedor, es del venue.
export function camposRequeridos({
  unidad = "evento",
  incluidoEnRenta = false,
  proveedorId = null,
  itemizado = false,
  tipo = "normal",
} = {}) {
  const req = ["nombre"];
  // Un descuento puede vivir sin monto fijo: se captura al aplicarlo.
  if (!incluidoEnRenta && tipo !== "descuento") req.push("precio");
  if (unidad === "otra") req.push("unidadNombre");
  // Un proveedor o piezas identificadas no exigen nada más: el costo del
  // proveedor y la escala de las piezas se capturan cuando se conozcan.
  void proveedorId;
  void itemizado;
  return req;
}

// Validación única del formulario de servicio. Devuelve el primer problema
// en español, o null si está listo para guardarse. `form` es la forma que
// comparten el alta, la edición y el asistente de clasificación.
export function validarServicio(form = {}) {
  const req = camposRequeridos({
    unidad: form.unidad,
    incluidoEnRenta: form.incluidoEnRenta,
    proveedorId: form.proveedorId,
    itemizado: form.itemizado,
    tipo: form.tipo || "normal",
  });
  if (!String(form.nombre || "").trim()) return "Falta el nombre del servicio.";
  if (req.includes("unidadNombre") && !String(form.unidadNombre || "").trim())
    return "Ponle nombre a la unidad de cobro (tanque, carga, persona…).";
  if (req.includes("precio") && !(Number(form.precioUnitario) > 0))
    return "Falta en cuánto lo cobras: sin precio no se puede cotizar. Si no se cobra por separado, marca “Está incluido en la renta del venue”.";
  if (form.proveedorId && Number(form.costoProveedor) < 0)
    return "El costo del proveedor no puede ser negativo.";
  if (form.proveedorId && form.itemizado)
    return "Un servicio que trae un proveedor no lleva piezas tuyas: las piezas se administran solo en lo que es del venue.";
  if ((form.tipo || "normal") === "renta") {
    if (form.proveedorId)
      return "La renta de un espacio es del venue: si la da un tercero, dalo de alta como servicio de proveedor.";
    if (form.itemizado)
      return "La renta de un espacio no lleva piezas: las piezas van en los servicios que la renta incluye.";
  }
  if (form.itemizado && (form.modoPiezas || "identificadas") === "cantidad") {
    const total = Math.floor(Number(form.piezasTotales) || 0);
    if (!(total > 0))
      return "¿Cuántas piezas tienes en total? Sin ese número no se puede saber cuántas quedan libres en una fecha.";
    const fuera = Array.isArray(form.noDisponibles) ? form.noDisponibles : [];
    let suma = 0;
    for (const nd of fuera) {
      const cantidad = Math.floor(Number(nd?.cantidad) || 0);
      if (!String(nd?.nombre || "").trim())
        return "Ponle nombre a cada renglón de piezas no disponibles (Mantenimiento, Bodega, Prestadas…).";
      if (!(cantidad > 0))
        return `“${nd.nombre}”: ¿cuántas piezas? Un renglón en cero no resta nada.`;
      suma += cantidad;
    }
    if (suma > total)
      return `Las piezas no disponibles (${suma}) son más que el total (${total}).`;
  }
  if (form.itemizado && (form.modoPiezas || "identificadas") !== "cantidad") {
    const tipo = form.escalaTipo || "ninguna";
    if (tipo === "etiquetas") {
      const etiquetas = (Array.isArray(form.escalaEtiquetas) ? form.escalaEtiquetas : [])
        .map((x) => String(x || "").trim())
        .filter(Boolean);
      if (etiquetas.length < 2) return "La escala por palabra necesita al menos dos niveles.";
    }
  }
  for (const v of Array.isArray(form.variantes) ? form.variantes : []) {
    if (!String(v?.nombre || "").trim()) return "Cada variante necesita un nombre.";
    // Sin precio propio, la variante hereda el del servicio.
    if (Number(v?.precioUnitario) < 0)
      return `Variante “${v.nombre}”: el precio no puede ser negativo.`;
    if (Number(v?.costoProveedor) < 0)
      return `Costo de la variante “${v.nombre}”: no puede ser negativo.`;
  }
  return null;
}

// ── Ingresos y recuperación de la inversión ──────────────────────────

const aFecha = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  if (typeof v?.toDate === "function") return v.toDate();
  if (typeof v?.seconds === "number") return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

// Ingresos generados por un servicio: la suma de las líneas del desglose que
// salen de él, SIN IVA (el IVA cobrado no es ingreso del venue), a lo largo
// de todos los eventos, con el desglose por variante (FRA-350: "¿cuánto
// vendí de renta de salón?" suma las tres y permite ver cada una). Es un
// scan en memoria sobre los eventos que VenueProvider ya tiene suscritos.
export function ingresosPorItem({ events = [], itemId, conceptos = [] }) {
  if (!itemId) return { total: 0, eventos: [], variantes: {} };
  const conceptosPorId = {};
  (conceptos || []).forEach((c) => {
    if (c?.id) conceptosPorId[c.id] = c;
  });
  const item = conceptosPorId[itemId] || null;

  const eventos = [];
  const variantes = {};
  (events || []).forEach((ev) => {
    if (!ev || ev.status === "cancelado") return;
    const desglose = Array.isArray(ev.desglose) ? ev.desglose : [];
    let monto = 0;
    desglose.forEach((linea) => {
      // Se compara el enlace directo de la línea al catálogo, no su
      // "respaldo": un servicio de proveedor respalda en el proveedor, pero
      // lo cobrado sigue siendo de ESTE renglón.
      if (linea?.conceptoId === itemId) {
        const importe = Number(linea.units) * Number(linea.unitPrice) || 0;
        monto += importe;
        const key = linea.varianteId || "_base";
        const acc =
          variantes[key] ||
          (variantes[key] = {
            varianteId: linea.varianteId || null,
            nombre: linea.varianteId
              ? varianteDe(item, linea.varianteId)?.nombre || "Variante eliminada"
              : "Sin variante",
            total: 0,
            lineas: 0,
          });
        acc.total += importe;
        acc.lineas += 1;
      }
    });
    if (monto > 0) {
      eventos.push({
        eventId: ev.id,
        nombre: ev.nombre || "Sin nombre",
        startTime: ev.startTime,
        monto,
      });
    }
  });

  eventos.sort((a, b) => (aFecha(a.startTime) || 0) - (aFecha(b.startTime) || 0));

  return {
    total: eventos.reduce((s, e) => s + e.monto, 0),
    eventos,
    variantes,
  };
}

// ¿Ya se pagó solo? Lo honesto no es comparar la inversión contra lo cobrado
// a secas, sino contra lo cobrado MENOS lo que costó usarlo (propano,
// mantenimientos). Un calentador que cobra $1,500 pero quema $360 de gas
// tarda casi 30% más en pagarse, y esa es la cifra que el owner necesita.
export function recuperacionItem({ item, ingresos, costos = [] }) {
  const adquisicion = adquisicionDe(item);
  const inversion = Number(adquisicion?.monto) || 0;
  if (inversion <= 0) return null;

  // La compra del activo (`adquisicion`) no cuenta como costo de uso: es
  // justo lo que se está recuperando. Todo lo demás que se le haya cargado
  // al servicio sí —recargas, refacciones, mantenimiento— venga del gasto
  // general o de un evento.
  const costosDeUso = (costos || [])
    .filter(
      (c) =>
        c.conceptoId === item.id &&
        c.origen !== "adquisicion"
    )
    .reduce((s, c) => s + (Number(c.monto) || 0), 0);

  const lista = ingresos?.eventos || [];
  const cobrado = ingresos?.total || 0;
  const neto = cobrado - costosDeUso;

  // El evento en el que cruzó el umbral. Se recorre en orden cronológico
  // repartiendo el costo de uso de forma proporcional a lo cobrado, que es
  // lo más cercano a la realidad sin llevar contabilidad por evento.
  const factor = cobrado > 0 ? neto / cobrado : 0;
  let acumulado = 0;
  let pagadoEn = null;
  for (const ev of lista) {
    acumulado += ev.monto * factor;
    if (acumulado >= inversion) {
      pagadoEn = ev;
      break;
    }
  }

  const desde = aFecha(adquisicion?.fecha);
  const hasta = pagadoEn ? aFecha(pagadoEn.startTime) : null;
  const dias =
    desde && hasta ? Math.max(0, Math.round((hasta - desde) / 86400000)) : null;

  return {
    inversion,
    cobrado,
    costosDeUso,
    recuperado: neto,
    falta: Math.max(0, inversion - neto),
    pct: inversion > 0 ? Math.min(1, Math.max(0, neto / inversion)) : 0,
    eventos: lista.length,
    pagadoEn,
    dias,
    meses: dias != null ? Math.round((dias / 30.44) * 10) / 10 : null,
  };
}
